import { useQuery } from '@apollo/client';
import ErrorIcon from '@atlaskit/icon/glyph/error';
import { R300 } from '@atlaskit/theme/colors';
import { Checkbox } from '@atlaskit/checkbox';
import cloneDeep from 'lodash/cloneDeep';
import {
  BillingStatus,
  FetchLinkedEntitlementsForTableQuery,
  FetchLinkedEntitlementsForTableQueryVariables,
  LinkedDestinationEntitlement,
  LinkedEntitlementTypes,
  LinkedProductTypes,
  PricingType,
  Maybe,
} from '@css-xp/graphtypes/src/generated-types-new';
import { NOT_AVAILABLE, EntitlementEdition } from '@atlassian/cssxp-utils-entitlement';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CustomPanel, DynamicResultTable, EntitlementIcon, withApollo, SandboxLozenge } from '@css-xp/base';
import { getProductName, fetchLinkedEntitlementsForTable } from '@css-xp/base/entitlement-utils';
import './undo-scheduled-cancel-apps-table.scss';

const PARENT_CHECKBOX_ID = 'select-all';

interface Props {
  entitlementId: string;
  isApp: boolean;
  message1?: string;
  message2?: string;
  header?: string;
  currentEntitlementType?: LinkedEntitlementTypes;
  destinationEntitlementType: Array<LinkedEntitlementTypes>;
  productType?: Maybe<LinkedProductTypes>;
  parentContainerID?: string;
  checkForLastParentExistence?: boolean;
  findProducts?: boolean;
  findApps?: boolean;
  findAllEntitlements?: boolean;
  updateLinkedEntitlements?: (data: any) => void;
  setLoading?: (data: boolean) => void;
  linkedEntsCount?: (data: any) => void;
  updateSelectedLinkedEntitlements?: (data: Array<LinkedDestinationEntitlement>) => void;
}

const emptyStateOrderProp = {
  header: '',
  description: 'No other subscriptions will be affected',
  imageUrl: '',
  testId: 'no-results-subscriptions-apps',
};

export const UndoScheduledCancelAppsTable = ({
  entitlementId,
  isApp,
  message1,
  message2,
  header,
  parentContainerID,
  currentEntitlementType,
  destinationEntitlementType,
  productType,
  findApps = false,
  findProducts = false,
  checkForLastParentExistence = false,
  updateLinkedEntitlements,
  setLoading,
  linkedEntsCount,
  updateSelectedLinkedEntitlements,
}: Props) => {
  const [checkedItems, setCheckedItems] = useState<Record<string, boolean>>({ [PARENT_CHECKBOX_ID]: false });
  const [selectedEntitlements, setSelectedEntitlements] = useState<Array<LinkedDestinationEntitlement>>([]);
  const [highlightedRowIndexes, updatehighlightedRowIndexes] = useState<number[]>([]);
  const { data, loading, error } = useQuery<
    FetchLinkedEntitlementsForTableQuery,
    FetchLinkedEntitlementsForTableQueryVariables
  >(fetchLinkedEntitlementsForTable, {
    variables: {
      id: entitlementId,
      searchLinkedEntitlementParams: {
        entitlementId: entitlementId,
        containerId: parentContainerID ?? undefined,
        currentEntitlementType,
        destinationEntitlementType,
        productType,
        findApps,
        findProducts,
        checkForLastParentExistence,
      },
    },
    skip: isApp,
    displayName: 'fetchAllLinkedEntitlements',
    notifyOnNetworkStatusChange: true,
  });

  const linkedEntitlements: Array<LinkedDestinationEntitlement> = useMemo(() => {
    return (
      (data?.entitlementDetails.linkedEntitlements?.filter(
        (linkedEntitlementObj) => linkedEntitlementObj?.billingStatus === BillingStatus.ACTIVE
      ) as Array<LinkedDestinationEntitlement>) || []
    );
  }, [data]);

  const visibleLinkedEnts = useMemo(() => {
    const filterLinkedEntsWithoutContainerId = linkedEntitlements.filter(
      (linkedEnt) =>
        !(
          (linkedEnt.isSandbox && linkedEnt.edition?.toUpperCase() === EntitlementEdition.FAMILY_SANDBOX) ||
          linkedEnt.entitlementId === parentContainerID
        )
    );

    return filterLinkedEntsWithoutContainerId;
  }, [linkedEntitlements]);

  useEffect(() => {
    setLoading && setLoading(loading);
  }, [loading, setLoading]);

  useEffect(() => {
    linkedEntsCount && visibleLinkedEnts.length > 0 && linkedEntsCount(1 + visibleLinkedEnts.length);
  }, [visibleLinkedEnts, linkedEntsCount]);

  useEffect(() => {
    if (linkedEntitlements?.length > 0) {
      updateLinkedEntitlements && updateLinkedEntitlements(linkedEntitlements);

      const updatedCheckedItems = {
        ...checkedItems,
      };
      const selectedEntitlememts: LinkedDestinationEntitlement[] = [];
      linkedEntitlements.forEach((item: LinkedDestinationEntitlement) => {
        if (isParentContainer(item)) {
          selectedEntitlememts.push(item);
        } else if (isPricingTypeFree(item)) {
          updatedCheckedItems[item.entitlementId] = true;
          selectedEntitlememts.push(item);
        } else {
          updatedCheckedItems[item.entitlementId] = false;
        }
      });

      updateSelectedLinkedEntitlements &&
        updateSelectedLinkedEntitlements(selectedEntitlememts as [LinkedDestinationEntitlement]);
      setSelectedEntitlements(selectedEntitlememts as [LinkedDestinationEntitlement]);
      setCheckedItems(updatedCheckedItems);
    }
  }, [linkedEntitlements]);

  const getIsParentIndeterminate = useCallback(
    (checkedItems: Record<string, boolean>) => {
      const checkedChildrenCount = getCheckedChildrenCount(checkedItems);
      return checkedChildrenCount > 0 && checkedChildrenCount < visibleLinkedEnts?.length;
    },
    [visibleLinkedEnts]
  );

  const getCheckedChildrenCount = useCallback((checkedItems: Record<string, boolean>) => {
    const childItems = Object.keys(checkedItems).filter((key) => key !== PARENT_CHECKBOX_ID && checkedItems[key]);
    return childItems.length;
  }, []);

  const isPricingTypeFree = useCallback(
    (item: LinkedDestinationEntitlement) => item?.offering?.pricingType === PricingType.FREE,
    []
  );

  const isParentContainer = useCallback(
    (item: LinkedDestinationEntitlement) =>
      (item.isSandbox && item.edition?.toUpperCase() === EntitlementEdition.FAMILY_SANDBOX) ||
      item.entitlementId === parentContainerID,
    []
  );

  const onCheckboxChange = (item: any, index?: number) => {
    let updatedSelectedEntitlements: LinkedDestinationEntitlement[];
    let updatedHighlightedRowIndexes: number[];

    if (item === PARENT_CHECKBOX_ID) {
      const newCheckedState = !checkedItems[PARENT_CHECKBOX_ID];
      const newCheckedItems: Record<string, boolean> = {
        ...checkedItems,
        [PARENT_CHECKBOX_ID]: newCheckedState,
      };
      updatedSelectedEntitlements = [];
      updatedHighlightedRowIndexes = [];

      linkedEntitlements.forEach((item: LinkedDestinationEntitlement) => {
        if (isPricingTypeFree(item) || isParentContainer(item)) {
          updatedSelectedEntitlements.push(item);
        } else {
          if (newCheckedState) {
            updatedSelectedEntitlements.push(item);
            updatedHighlightedRowIndexes = visibleLinkedEnts.map((_, index) => index);
          }

          newCheckedItems[item.entitlementId] = newCheckedState;
        }
      });

      setCheckedItems(newCheckedItems);
    } else {
      const newCheckedItems = {
        ...checkedItems,
        [item.entitlementId]: !checkedItems[item.entitlementId],
      };
      updatedSelectedEntitlements = cloneDeep(selectedEntitlements);
      updatedHighlightedRowIndexes = [...highlightedRowIndexes];

      if (!checkedItems[item.entitlementId]) {
        updatedSelectedEntitlements.push(item);
        updatedHighlightedRowIndexes.push(index as number);
      } else {
        const removedHighlightedRowIndex = updatedHighlightedRowIndexes?.findIndex((value) => value === index);
        updatedHighlightedRowIndexes.splice(removedHighlightedRowIndex, 1);
        const removedEntitlementIndex = updatedSelectedEntitlements?.findIndex(
          (ent) => ent.entitlementId === item.entitlementId
        );
        updatedSelectedEntitlements.splice(removedEntitlementIndex, 1);
      }

      setCheckedItems({
        ...newCheckedItems,
        [PARENT_CHECKBOX_ID]: getCheckedChildrenCount(newCheckedItems) > 0,
      });
    }

    updatehighlightedRowIndexes(updatedHighlightedRowIndexes);
    setSelectedEntitlements(updatedSelectedEntitlements as LinkedDestinationEntitlement[]);
    updateSelectedLinkedEntitlements &&
      updateSelectedLinkedEntitlements(updatedSelectedEntitlements as [LinkedDestinationEntitlement]);
  };

  const getRows = () => {
    return visibleLinkedEnts.map((item: LinkedDestinationEntitlement, index) => {
      const isFreeSubscription = isPricingTypeFree(item);
      const cellData = [
        {
          content: (
            <>
              <EntitlementIcon
                logoId={item?.logoId as string}
                productId={item?.productId as string}
                isCloud={true}
                className="avatar medium"
              />{' '}
              <span className="product-name">{getProductName(item)}</span>
              {item?.isSandbox && <SandboxLozenge />}
              <div className="edition-label">{item?.edition ?? ''}</div>
            </>
          ),
        },
        {
          content: item?.entitlementNumber ? (
            <span className="entitlement-number">{item?.entitlementNumber}</span>
          ) : (
            <span className="entitlement-number" aria-label="Not available" data-testid="not-available">
              {NOT_AVAILABLE}
            </span>
          ),
        },
        {
          content: (
            <Checkbox
              isDisabled={isFreeSubscription}
              isChecked={checkedItems[item.entitlementId]}
              onChange={() => onCheckboxChange(item, index)}
              value={item.entitlementId}
              label="Undo"
              name={`linked-ent-checkbox-${index}`}
              testId={`linked-ent-checkbox-${index}`}
            />
          ),
        },
      ];

      return {
        key: item.entitlementId ? item.entitlementId : index.toString(),
        cells: cellData,
        isHighlighted: isFreeSubscription || highlightedRowIndexes.some((value) => value === index),
      };
    });
  };

  const getHead = () => {
    const cells = [
      {
        content: 'Product',
        shouldTruncate: true,
        width: 40,
      },
      {
        content: 'Entitlement number',
        width: 20,
      },
      {
        content: (
          <Checkbox
            isChecked={checkedItems[PARENT_CHECKBOX_ID]}
            isIndeterminate={getIsParentIndeterminate(checkedItems)}
            onChange={() => onCheckboxChange(PARENT_CHECKBOX_ID)}
            label="Select All"
            value={'select-all'}
            name="select-all"
            isDisabled={visibleLinkedEnts?.length < 1}
            testId="select-all-linked-ent-checkbox"
          />
        ),
        width: 15,
      },
    ];

    return {
      cells,
    };
  };

  return (
    <div className="undo-scheduled-cancel-apps-container">
      <div>
        {' '}
        <p className="n-300">
          {message1 ? message1 : 'All the following subscriptions and the apps will also be affected with this action'}
        </p>
      </div>
      <CustomPanel
        testId="linked-ents"
        expandedHeader={header ? header : `View subscriptions`}
        collapsedHeader={header ? header : `View subscriptions`}
      >
        {error ? (
          <div className="error-block">
            <ErrorIcon label="Error" size="medium" primaryColor={R300} />
            <span className="error-text">
              It looks like something went wrong. <span className="n-300">Please go back and try again.</span>
            </span>
          </div>
        ) : (
          <>
            {message2 && <h4 className="undo-scheduled-cancel-apps-table-heading">{message2}</h4>}
            <DynamicResultTable
              boxShadow={false}
              testId="undo-scheduled-cancel-apps-table"
              head={getHead()}
              rows={getRows()}
              emptyStateProps={emptyStateOrderProp}
              isLoading={loading}
              alternateRowColor={false}
            />
          </>
        )}
      </CustomPanel>
    </div>
  );
};

export default withApollo(UndoScheduledCancelAppsTable);
